// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { IdentificationCard } from "@phosphor-icons/react";
import { Button, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

export const ASK_V2_WELCOME_CHIPS = [
  { id: "know", labelKey: "askV2.home.welcome.know" },
  { id: "personalize", labelKey: "askV2.home.welcome.personalize" },
  { id: "canDo", labelKey: "askV2.home.welcome.canDo" },
] as const;

const LEAD_ID = "ask-v2-welcome-lead";

type WelcomeBriefingProps = {
  disabled?: boolean;
  onSelect: (prompt: string) => void;
  /** Personalized get-started line from christo; null keeps the translated default. */
  lead?: string | null;
  /** Skeleton the line until christo answers; chips stay visible. */
  pending?: boolean;
};

function WelcomeLeadSkeleton() {
  return (
    <div
      data-testid="ask-v2-welcome-copy-skeleton"
      className="flex w-full justify-center"
      aria-hidden="true"
    >
      <div className="h-5 w-[min(100%,20rem)] animate-pulse rounded bg-puntt-neutral-gray-6" />
    </div>
  );
}

function personalizedOrDefault(
  personalized: string | null | undefined,
  fallback: string,
): { text: string; personalized: boolean } {
  const trimmed = personalized?.trim();
  return trimmed
    ? { text: trimmed, personalized: true }
    : { text: fallback, personalized: false };
}

/**
 * The one line between the greeting and the suggestion chips. The design
 * review asked for a single lead-in ("Here are some ways to get started"),
 * not a heading with a sub-heading reciting what Ask Puntt can do, so this
 * renders plain text and the section takes its name from that line.
 */
export function WelcomeBriefing({
  disabled = false,
  onSelect,
  lead = null,
  pending = false,
}: WelcomeBriefingProps) {
  const { t } = useTranslation("ui");
  const line = personalizedOrDefault(lead, t("askV2.home.welcome.lead"));

  return (
    <section
      data-testid="ask-v2-welcome"
      data-personalized={line.personalized ? "true" : "false"}
      data-pending={pending ? "true" : "false"}
      aria-busy={pending}
      aria-label={pending ? t("askV2.home.welcome.lead") : undefined}
      aria-labelledby={pending ? undefined : LEAD_ID}
      className="flex w-full max-w-3xl flex-col items-center gap-3 text-center"
    >
      {pending ? (
        <WelcomeLeadSkeleton />
      ) : (
        <Text
          as="p"
          id={LEAD_ID}
          size="3"
          className="text-puntt-neutral-gray-11"
          data-testid="ask-v2-welcome-lead"
        >
          {line.text}
        </Text>
      )}
      <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
        {ASK_V2_WELCOME_CHIPS.map((chip) => {
          const label = t(chip.labelKey);
          return (
            <Button
              key={chip.id}
              type="button"
              size="3"
              variant="outline"
              data-testid={`ask-v2-welcome-chip-${chip.id}`}
              data-chip={chip.id}
              disabled={disabled}
              onClick={() => onSelect(label)}
            >
              <IdentificationCard />
              {label}
            </Button>
          );
        })}
      </div>
    </section>
  );
}
